import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

// A small canvas where the user draws a signature with the mouse (or finger).
// Exposes { isEmpty, clear, toDataURL } via ref; calls onChange(hasInk) as the
// user starts/clears drawing so a parent can enable its submit button.
const SignaturePad = forwardRef(({ onChange, className = "" }, ref) => {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const hasInk = useRef(false);
    const last = useRef({ x: 0, y: 0 });

    const ctxSetup = (canvas) => {
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.max(1, Math.round(rect.width * dpr));
        canvas.height = Math.max(1, Math.round(rect.height * dpr));
        const ctx = canvas.getContext("2d");
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        // Solid white backing so the exported PNG is legible on any background.
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.lineWidth = 2.4;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#111827";
        return ctx;
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        ctxSetup(canvas);
        const onResize = () => { ctxSetup(canvas); hasInk.current = false; if (onChange) onChange(false); };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(ref, () => ({
        isEmpty: () => !hasInk.current,
        clear: () => {
            const c = canvasRef.current;
            if (!c) return;
            const ctx = c.getContext("2d");
            ctx.save();
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, c.width, c.height);
            ctx.restore();
            hasInk.current = false;
            if (onChange) onChange(false);
        },
        toDataURL: () => (canvasRef.current ? canvasRef.current.toDataURL("image/png") : null),
    }));

    const point = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const src = e.touches && e.touches.length ? e.touches[0] : e;
        return { x: src.clientX - rect.left, y: src.clientY - rect.top };
    };

    const start = (e) => {
        e.preventDefault();
        drawing.current = true;
        last.current = point(e);
    };
    const move = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext("2d");
        const p = point(e);
        ctx.beginPath();
        ctx.moveTo(last.current.x, last.current.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        last.current = p;
        if (!hasInk.current) { hasInk.current = true; if (onChange) onChange(true); }
    };
    const end = () => { drawing.current = false; };

    return (
        <canvas
            ref={canvasRef}
            className={`sigpad-canvas ${className}`}
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
        />
    );
});

export default SignaturePad;
